/**
 * shadcn/ui Sidebar -- the new-york style from the Tailwind 3 line (`npx shadcn@2.3.0 add sidebar`), adapted for eMerge.
 * Same anatomy and names (SidebarProvider, Sidebar, SidebarHeader, SidebarContent, SidebarGroup, SidebarGroupLabel,
 * SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarMenuBadge, SidebarTrigger, SidebarInset, useSidebar), the same
 * `sidebar-*` colour tokens and `group-data-[collapsible=icon]` styling. Five departures from the generated file, each
 * because the generated behaviour breaks something verified in this product (docs/design/kanban.md, "Workspace sidebar"):
 *
 *  1. In flow, not fixed. The rail is a flex column whose body is sticky, so it sits under the portal's top bar instead
 *     of over it and the page-as-frame boards keep their frame (the generated one is `fixed inset-y-0 h-svh`).
 *  2. The collapsed choice is kept per device in localStorage['emerge:workspace-sidebar'], best effort (generated: a
 *     cookie sent with every API request).
 *  3. No Ctrl/Cmd+B shortcut: the note editors use it for bold.
 *  4. Below 768px the sheet is drawn in place -- Radix FocusScope plus the board kit's useSheetModal -- and closes in
 *     the same render as any location change, so a record opened by URL is never a second modal (verification of #414).
 *     The generated Sheet is a portalled Radix Dialog, which cannot keep that invariant.
 *  5. Motion honours prefers-reduced-motion (Tailwind `motion-safe:`; framer-motion's useReducedMotion for the sheet).
 *
 * Stable hooks kept from the earlier sidebar for tests and the harness: .wsb-layout, .wsb (data-state), .wsb-body,
 * .wsb-inset, .wsb-trigger, .wsb-sheet[role=dialog], .wsb-overlay, .wsb-link, .wsb-label.
 */
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { FocusScope } from '@radix-ui/react-focus-scope';
import { motion, useReducedMotion } from 'framer-motion';
import { PanelLeft, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import useSheetModal from '../../board/useSheetModal';
import { cn } from '../lib/utils';
import { Button } from './button';
import { Separator } from './separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';

export const SIDEBAR_STORAGE_KEY = 'emerge:workspace-sidebar';
/** Below this the sidebar is a sheet (shadcn's md breakpoint). */
export const SIDEBAR_MOBILE_QUERY = '(max-width: 767px)';
const SIDEBAR_WIDTH = '15rem';
const SIDEBAR_WIDTH_ICON = '3.5rem';

/* ── State ──────────────────────────────────────────────────────────── */

export interface SidebarContextProps {
  state: 'expanded' | 'collapsed';
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  toggleSidebar: () => void;
  mobile: boolean;
  openMobile: boolean;
  setOpenMobile: (v: boolean) => void;
  railId: string;
  triggerRef: React.MutableRefObject<HTMLButtonElement | null>;
}

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

export function useSidebar(): SidebarContextProps {
  const ctx = React.useContext(SidebarContext);
  if (!ctx) throw new Error('useSidebar must be used inside <SidebarProvider>.');
  return ctx;
}

function readStored(): boolean {
  try { return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'collapsed'; } catch { return false; }
}
function writeStored(collapsed: boolean): void {
  try { window.localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? 'collapsed' : 'expanded'); } catch { /* private window, blocked storage: the choice lasts this page only */ }
}

export function useMediaQuery(query: string): boolean {
  const read = () => {
    try { return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches; } catch { return false; }
  };
  const [matches, setMatches] = React.useState(read);
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    if (typeof mq.addEventListener === 'function') { mq.addEventListener('change', on); return () => mq.removeEventListener('change', on); }
    mq.addListener(on);
    return () => mq.removeListener(on);
  }, [query]);
  return matches;
}

export function SidebarProvider({ className, style, children }: { className?: string; style?: React.CSSProperties; children: React.ReactNode }) {
  const mobile = useMediaQuery(SIDEBAR_MOBILE_QUERY);
  const [collapsed, setCollapsedState] = React.useState<boolean>(readStored);
  /*
   * The sheet belongs to the location it was opened on. Any location change -- a view chosen, Back, a URL that opens a
   * record (`?case=`, `?open=`) -- closes it in the SAME render that shows the new location, rather than in an effect
   * after it: the record sheet's modality is applied in that commit's layout phase, after this sheet has gone.
   */
  const locationKey = useLocation().key;
  const keyRef = React.useRef(locationKey);
  keyRef.current = locationKey;
  const [openAt, setOpenAt] = React.useState<string | null>(null);
  const openMobile = openAt !== null && openAt === locationKey;
  const setOpenMobile = React.useCallback((v: boolean) => setOpenAt(v ? keyRef.current : null), []);
  const railId = React.useId();
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const setCollapsed = React.useCallback((v: boolean) => { setCollapsedState(v); writeStored(v); }, []);
  const toggleSidebar = React.useCallback(() => {
    if (mobile) setOpenMobile(!openMobile); else setCollapsed(!collapsed);
  }, [mobile, openMobile, collapsed, setOpenMobile, setCollapsed]);
  // Leaving the phone layout closes the sheet; the rail takes over.
  React.useEffect(() => { if (!mobile) setOpenMobile(false); }, [mobile, setOpenMobile]);
  const value = React.useMemo<SidebarContextProps>(() => ({
    state: collapsed ? 'collapsed' : 'expanded', collapsed, setCollapsed, toggleSidebar, mobile, openMobile, setOpenMobile, railId, triggerRef,
  }), [collapsed, setCollapsed, toggleSidebar, mobile, openMobile, setOpenMobile, railId]);

  return (
    <SidebarContext.Provider value={value}>
      <TooltipProvider delayDuration={150}>
        <div
          className={cn('wsb-layout group/sidebar-wrapper flex min-w-0 items-stretch', className)}
          data-sidebar={mobile ? 'sheet' : collapsed ? 'collapsed' : 'expanded'}
          style={{ '--sidebar-width': SIDEBAR_WIDTH, '--sidebar-width-icon': SIDEBAR_WIDTH_ICON, ...style } as React.CSSProperties}
        >
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext.Provider>
  );
}

/* ── The sidebar: a rail on desktop, a sheet on a phone ─────────────── */

/** The sheet's name for assistive technology ("UMED navigation"). Required, because a dialog must be named. */
export function Sidebar({ title, className, children }: { title: string; className?: string; children: React.ReactNode }) {
  const { mobile, collapsed, openMobile, setOpenMobile, railId, triggerRef } = useSidebar();
  const close = React.useCallback(() => setOpenMobile(false), [setOpenMobile]);
  if (mobile) {
    return openMobile ? <SidebarSheet title={title} onClose={close} triggerRef={triggerRef}>{children}</SidebarSheet> : null;
  }
  return (
    <div
      id={railId}
      data-sidebar="sidebar"
      data-state={collapsed ? 'collapsed' : 'expanded'}
      data-collapsible={collapsed ? 'icon' : ''}
      className={cn(
        'wsb group relative z-[11] flex shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground',
        'w-[--sidebar-width] data-[state=collapsed]:w-[--sidebar-width-icon]',
        'motion-safe:transition-[width] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.33,1,0.68,1)]',
        className,
      )}
    >
      {/* The rail's contents stay on screen while the page scrolls under them. Collapsed, popovers beside the icons
          must not be clipped, so the body stops scrolling. */}
      <div className="wsb-body sticky top-0 flex max-h-screen flex-col overflow-y-auto overflow-x-hidden [scrollbar-width:thin] group-data-[collapsible=icon]:overflow-visible">
        {children}
      </div>
    </div>
  );
}

/**
 * The phone sheet: a modal from the left. Focus is trapped (Radix FocusScope) and goes back to the trigger when it
 * closes; everything behind it is inert and hidden from assistive technology, and the page does not scroll (the board
 * kit's useSheetModal). Escape, the close button, the backdrop and choosing a view each close it. Escape is the record
 * sheet's while one is open (`.board-dock[aria-modal]`). Enter-only motion: an exit animation would keep a second modal
 * on screen.
 */
function SidebarSheet({ title, onClose, triggerRef, children }: {
  title: string; onClose: () => void; triggerRef: React.MutableRefObject<HTMLButtonElement | null>; children: React.ReactNode;
}) {
  const hostRef = React.useRef<HTMLDivElement | null>(null);
  const titleId = React.useId();
  const reduce = useReducedMotion();
  useSheetModal(true, () => hostRef.current);
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('.board-dock[aria-modal="true"]')) return;
      e.stopPropagation();
      onClose();
    };
    // Bubble phase: the workspace switcher's own Escape (capture) closes its list first and stops this one.
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div ref={hostRef} className="wsb-sheet-host">
      <motion.div
        className="wsb-overlay fixed inset-0 z-[60] bg-black/45"
        aria-hidden="true"
        onMouseDown={onClose}
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
      />
      <FocusScope asChild trapped loop
        onUnmountAutoFocus={(e) => {
          e.preventDefault();
          const t = triggerRef.current;
          if (t && !t.closest('[inert]')) t.focus();
        }}>
        <motion.div
          className="wsb-sheet fixed inset-y-0 left-0 z-[61] flex w-[min(18rem,calc(100vw-3.5rem))] flex-col overflow-y-auto overscroll-contain border-r border-sidebar-border bg-sidebar text-sidebar-foreground shadow-xl focus:outline-none"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          data-state="open"
          data-sidebar="sidebar"
          data-mobile="true"
          initial={reduce ? false : { x: '-100%' }}
          animate={{ x: 0 }}
          transition={{ type: 'spring', stiffness: 420, damping: 42, mass: 0.9 }}
        >
          <h2 id={titleId} className="sr-only">{title}</h2>
          <Button type="button" variant="ghost" size="icon" className="wsb-sheet-close absolute right-2 top-3 z-[1] text-sidebar-foreground/80" aria-label="Close navigation" onClick={onClose}>
            <X aria-hidden="true" focusable="false" />
          </Button>
          {children}
        </motion.div>
      </FocusScope>
    </div>
  );
}

/* ── Header trigger ─────────────────────────────────────────────────── */

/** The collapse / open control, for the page header beside the title. */
export function SidebarTrigger({ className }: { className?: string }) {
  const { collapsed, setCollapsed, mobile, openMobile, setOpenMobile, railId, triggerRef } = useSidebar();
  const label = mobile ? 'Open navigation' : collapsed ? 'Expand sidebar' : 'Collapse sidebar';
  return (
    <Button
      ref={triggerRef}
      type="button"
      variant="ghost"
      size="icon"
      data-sidebar="trigger"
      className={cn('wsb-trigger h-9 w-9 shrink-0 text-muted-foreground hover:text-foreground', className)}
      aria-label={label}
      title={mobile ? undefined : label}
      {...(mobile ? { 'aria-haspopup': 'dialog' as const, 'aria-expanded': openMobile } : { 'aria-controls': railId, 'aria-expanded': !collapsed })}
      onClick={() => (mobile ? setOpenMobile(true) : setCollapsed(!collapsed))}
    >
      <PanelLeft aria-hidden="true" focusable="false" />
    </Button>
  );
}

/* ── Regions ────────────────────────────────────────────────────────── */

export function SidebarInset({ className, ...props }: React.ComponentProps<'div'>) {
  return <div className={cn('wsb-inset flex min-w-0 flex-1 flex-col', className)} {...props} />;
}

export function SidebarHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-sidebar="header" className={cn('flex flex-col gap-2 border-b border-sidebar-border p-2.5 group-data-[collapsible=icon]:px-2', className)} {...props} />;
}

export function SidebarFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-sidebar="footer" className={cn('mt-auto flex flex-col gap-1 border-t border-sidebar-border p-2.5 group-data-[collapsible=icon]:px-2', className)} {...props} />;
}

export function SidebarContent({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-sidebar="content" className={cn('flex flex-[1_0_auto] flex-col gap-3 px-2.5 py-3 group-data-[collapsible=icon]:gap-2.5 group-data-[collapsible=icon]:px-2', className)} {...props} />;
}

export function SidebarSeparator({ className, ...props }: React.ComponentProps<typeof Separator>) {
  return <Separator data-sidebar="separator" className={cn('mx-2 w-auto bg-sidebar-border', className)} {...props} />;
}

export function SidebarGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-sidebar="group" className={cn('relative flex w-full min-w-0 flex-col group-data-[collapsible=icon]:[&+&]:border-t group-data-[collapsible=icon]:[&+&]:border-sidebar-border group-data-[collapsible=icon]:[&+&]:pt-2.5', className)} {...props} />;
}

export function SidebarGroupLabel({ className, ...props }: React.ComponentProps<'p'>) {
  return (
    <p
      data-sidebar="group-label"
      className={cn(
        'mb-1 flex h-6 shrink-0 items-center px-2.5 text-xs font-medium tracking-[0.01em] text-sidebar-foreground/75',
        'group-data-[collapsible=icon]:sr-only',
        className,
      )}
      {...props}
    />
  );
}

export function SidebarMenu({ className, ...props }: React.ComponentProps<'ul'>) {
  return <ul data-sidebar="menu" className={cn('m-0 flex w-full min-w-0 list-none flex-col gap-0.5 p-0', className)} {...props} />;
}

export function SidebarMenuItem({ className, ...props }: React.ComponentProps<'li'>) {
  return <li data-sidebar="menu-item" className={cn('group/menu-item relative', className)} {...props} />;
}

const menuButtonClass = cn(
  'wsb-link peer/menu-button relative flex w-full min-h-9 items-center gap-2.5 overflow-hidden rounded-md px-2.5 py-1.5 text-left text-sm font-medium no-underline',
  'text-sidebar-foreground/80 outline-none ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
  'focus-visible:ring-2 focus-visible:ring-inset',
  'motion-safe:transition-[background-color,color] motion-safe:duration-150',
  'data-[active=true]:bg-sidebar-accent data-[active=true]:font-semibold data-[active=true]:text-sidebar-primary data-[active=true]:shadow-[inset_2px_0_0_hsl(var(--sidebar-primary))]',
  '[&_svg]:size-[18px] [&_svg]:shrink-0',
  '[[data-mobile=true]_&]:min-h-11',
  'group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:size-10 group-data-[collapsible=icon]:min-h-10 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0',
  'group-data-[collapsible=icon]:data-[active=true]:shadow-[inset_0_0_0_1px_hsl(var(--sidebar-primary))]',
);

/**
 * One menu entry. Pass a router <Link> as the child with `asChild`, so the entry is a real link. When the rail is
 * collapsed the name shows beside the icon in a tooltip, on hover and keyboard focus; the link keeps its full
 * accessible name either way.
 */
export const SidebarMenuButton = React.forwardRef<HTMLAnchorElement, React.ComponentPropsWithoutRef<'a'> & {
  asChild?: boolean; isActive?: boolean; tooltip?: string;
}>(({ asChild = false, isActive = false, tooltip, className, ...props }, ref) => {
  const { collapsed, mobile } = useSidebar();
  const Comp = asChild ? Slot : 'a';
  const button = (
    <Comp ref={ref} data-sidebar="menu-button" data-active={isActive ? 'true' : undefined} className={cn(menuButtonClass, className)} {...props} />
  );
  // Only the collapsed rail has a tooltip. An open Radix tooltip -- even a hidden one, as the generated file keeps --
  // takes the next Escape (DismissableLayer), which would stop Escape closing the phone sheet or a record.
  if (!tooltip || !collapsed || mobile) return button;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right" align="center">{tooltip}</TooltipContent>
    </Tooltip>
  );
});
SidebarMenuButton.displayName = 'SidebarMenuButton';

/** A count beside an entry. Decorative: the entry's accessible name already says it. */
export function SidebarMenuBadge({ className, tone = 'default', ...props }: React.ComponentProps<'span'> & { tone?: 'default' | 'stop' }) {
  return (
    <span
      data-sidebar="menu-badge"
      aria-hidden="true"
      className={cn(
        'wsb-badge ml-auto inline-flex h-5 min-w-5 shrink-0 select-none items-center justify-center rounded-full px-1.5 text-[11.5px] font-semibold tabular-nums',
        tone === 'stop' ? 'stop bg-[var(--tide-status-danger-bg)] text-[var(--tide-status-danger)]' : 'bg-sidebar-border/60 text-sidebar-foreground',
        '[&+&]:ml-[-4px] group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
}
